import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { audit } from '@cwos/api-client';
import { SkeletonSet, SuccessEmpty } from '@cwos/ui-admin';
import { LocationSettingsShell } from './LocationSettingsShell.js';
import { LoadFailure } from '../LoadFailure.js';

/**
 * Activity — the hub's read of location-relevant audit events (design doc §1's
 * Activity section; L118).
 *
 * REUSE, NOT A FORK: this page is the EXISTING audit read surface
 * (`GET /audit/events`) filtered to the `location_privacy` category the audit
 * catalog already maintains — policy changes, geofence changes, exception
 * decisions, and every viewing of location history all land there today. No
 * new endpoint, no second projection, no separate log. The full Audit &
 * Compliance centre remains the place to investigate; this is the same record,
 * pre-filtered to this hub's subject.
 *
 * GATED EXACTLY AS THE AUDIT CENTRE IS: the server route demands `audit:read`
 * (org_admin only), so the hub shows this section only to a holder — a link
 * that could only 403 is a dead link with extra steps. The server checks
 * again regardless, and the projection inside it decides what names this
 * viewer may be told (the label gate travels with the caller's own authz).
 *
 * The rows are the server's own business-language sentences (`summary`), never
 * re-derived here — the domain that owns the meaning owns the sentence.
 */

const PAGE_SIZE = 50;

function formatInstant(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function LocationActivityPage() {
  // Keyset pagination, accumulated: `cursor` is the page being shown up to.
  const [cursors, setCursors] = useState<string[]>([]);

  const query = useQuery({
    queryKey: ['location', 'activity', cursors],
    queryFn: async () => {
      const pages = [await audit.events({ category: 'location_privacy', limit: PAGE_SIZE })];
      for (const cursor of cursors) {
        pages.push(await audit.events({ category: 'location_privacy', limit: PAGE_SIZE, cursor }));
      }
      return {
        events: pages.flatMap((p) => p.events),
        nextCursor: pages[pages.length - 1]!.nextCursor,
        labelNotice: pages[0]!.labelNotice,
      };
    },
  });

  return (
    <LocationSettingsShell active="activity">
      <section className="rn-card">
        <div className="rn-card-head">
          <h2 className="rn-card-title">Location activity</h2>
        </div>
        <p className="rn-muted" style={{ marginTop: 'var(--space-6)' }}>
          Every change to location policy, work sites, geofences and exception decisions — and every
          viewing of location history — is recorded in the organization’s activity log. This is that
          record, filtered to location. The full log lives in the Audit & Compliance centre.
        </p>

        {query.isLoading ? (
          <SkeletonSet surface="table" rows={6} />
        ) : query.isError && !query.data ? (
          <LoadFailure
            error={query.error}
            message="We couldn’t load the activity just now."
            onRetry={() => void query.refetch()}
          />
        ) : (query.data?.events.length ?? 0) === 0 ? (
          <SuccessEmpty
            heading="No location activity yet"
            message="When someone changes the location policy, edits a geofence, decides an exception, or views location history, it will appear here."
          />
        ) : (
          <>
            {query.data!.labelNotice ? (
              <p className="rn-muted" style={{ fontSize: 13, marginTop: 'var(--space-6)' }}>
                {query.data!.labelNotice}
              </p>
            ) : null}
            <ul className="cmp-controls" style={{ marginTop: 'var(--space-10)' }}>
              {query.data!.events.map((ev) => (
                <li key={ev.id} className="cmp-control">
                  <div className="cmp-control-head">
                    <span className="cmp-control-title">{ev.summary}</span>
                  </div>
                  <p className="rn-muted" style={{ fontSize: 13 }}>
                    {ev.actor.label} · {formatInstant(ev.occurredAt)}
                  </p>
                  {ev.note ? <p className="rn-muted cmp-control-desc">{ev.note}</p> : null}
                </li>
              ))}
            </ul>
            {query.data!.nextCursor ? (
              <button
                type="button"
                className="rn-btn-quiet"
                style={{ marginTop: 'var(--space-8)' }}
                onClick={() => setCursors((current) => [...current, query.data!.nextCursor!])}
              >
                Show earlier activity
              </button>
            ) : null}
          </>
        )}
      </section>
    </LocationSettingsShell>
  );
}
